import { Logger } from "./Logger";
import { QUESTION_DWELL_MS, QUESTION_ELEMENT_FINDER_INTERVAL, QUESTION_VIEWPORT_FILL_RATIO, QUESTION_VISIBLE_RATIO, } from "./variables";
class QuestionVisibilityTracker {
    constructor(options) {
        var _a, _b;
        this.logger = new Logger("QuestionVisibilityTracker");
        this.idByElement = new WeakMap();
        this.state = new Map();
        this.pending = new Map();
        this.entries = new Map();
        this.observers = new Map();
        this.finderIntervals = new Map();
        this.isTabFocused = false;
        this.observedCount = 0;
        this.scanCount = 0;
        this.hasWarnedAboutNothingObserved = false;
        this.onIntersect = (entries) => {
            entries.forEach((entry) => {
                const questionId = this.idByElement.get(entry.target);
                if (questionId == null) {
                    return;
                }
                this.entries.set(questionId, entry);
                this.evaluate(questionId);
            });
        };
        this.onFocusChange = () => {
            const isFocused = QuestionVisibilityTracker.readTabFocused();
            if (isFocused === this.isTabFocused) {
                return;
            }
            this.isTabFocused = isFocused;
            if (isFocused === false) {
                this.cancelAllPending();
                this.state.forEach((currentState, questionId) => {
                    if (currentState === "shown") {
                        this.state.set(questionId, "hidden");
                    }
                });
                return;
            }
            Array.from(this.entries.keys()).forEach((questionId) => this.evaluate(questionId));
        };
        this.source = options.source;
        this.onShown = options.onShown;
        this.threshold = (_a = options.source.threshold) !== null && _a !== void 0 ? _a : QUESTION_VISIBLE_RATIO;
        this.dwellMs = (_b = options.source.dwellMs) !== null && _b !== void 0 ? _b : QUESTION_DWELL_MS;
    }
    static buildThresholdSteps() {
        const steps = [];
        for (let ratio = 0; ratio <= 1; ratio += 0.1) {
            steps.push(Number(ratio.toFixed(1)));
        }
        return steps;
    }
    static readTabFocused() {
        return document.hidden === false && document.hasFocus();
    }
    static readViewHeight(doc) {
        var _a, _b;
        return (_b = (_a = doc === null || doc === void 0 ? void 0 : doc.defaultView) === null || _a === void 0 ? void 0 : _a.innerHeight) !== null && _b !== void 0 ? _b : window.innerHeight;
    }
    start() {
        this.isTabFocused = QuestionVisibilityTracker.readTabFocused();
        document.addEventListener("visibilitychange", this.onFocusChange);
        window.addEventListener("focus", this.onFocusChange);
        window.addEventListener("blur", this.onFocusChange);
        this.attachToDocument(document);
    }
    stop() {
        Array.from(this.observers.keys()).forEach((doc) => this.detachFromDocument(doc));
        this.cancelAllPending();
        this.state.clear();
        this.entries.clear();
        document.removeEventListener("visibilitychange", this.onFocusChange);
        window.removeEventListener("focus", this.onFocusChange);
        window.removeEventListener("blur", this.onFocusChange);
    }
    attachToDocument(doc) {
        if (this.observers.has(doc)) {
            return;
        }
        const view = doc.defaultView;
        if (view == null || typeof view.IntersectionObserver === "undefined") {
            return;
        }
        const observer = new view.IntersectionObserver(this.onIntersect, {
            root: null,
            rootMargin: "0px",
            threshold: QuestionVisibilityTracker.buildThresholdSteps(),
        });
        this.observers.set(doc, observer);
        this.scanCount = 0;
        this.observeNewElementsIn(doc);
        const intervalId = setInterval(() => this.observeNewElementsIn(doc), QUESTION_ELEMENT_FINDER_INTERVAL);
        this.finderIntervals.set(doc, intervalId);
    }
    detachFromDocument(doc) {
        const observer = this.observers.get(doc);
        if (observer != null) {
            observer.disconnect();
            this.observers.delete(doc);
        }
        const intervalId = this.finderIntervals.get(doc);
        if (intervalId != null) {
            clearInterval(intervalId);
            this.finderIntervals.delete(doc);
        }
        this.forgetQuestionsOf(doc);
    }
    forgetQuestionsOf(doc) {
        Array.from(this.entries.entries()).forEach(([questionId, entry]) => {
            if (entry.target.ownerDocument !== doc) {
                return;
            }
            this.cancelPending(questionId);
            this.entries.delete(questionId);
            this.state.set(questionId, "hidden");
        });
    }
    observeNewElementsIn(doc) {
        const observer = this.observers.get(doc);
        if (observer == null) {
            return;
        }
        const elements = doc.querySelectorAll(this.source.selector);
        this.scanCount += 1;
        Array.from(elements).forEach((element) => {
            if (this.idByElement.has(element)) {
                return;
            }
            const questionId = this.source.resolveId(element);
            if (questionId == null) {
                return;
            }
            this.idByElement.set(element, questionId);
            this.observedCount += 1;
            if (this.state.has(questionId) === false) {
                this.state.set(questionId, "hidden");
            }
            observer.observe(element);
        });
        this.warnIfNothingObserved(elements.length);
    }
    warnIfNothingObserved(matchCount) {
        if (this.hasWarnedAboutNothingObserved || this.observedCount > 0) {
            return;
        }
        if (this.scanCount < QuestionVisibilityTracker.SCANS_BEFORE_WARNING) {
            return;
        }
        this.hasWarnedAboutNothingObserved = true;
        if (matchCount === 0) {
            this.logger.warn(`no element matched "${this.source.selector}" in any of the ` +
                `${this.observers.size} attached document(s) after ${this.scanCount} scans.`);
            return;
        }
        this.logger.warn(`${matchCount} element(s) matched "${this.source.selector}" but resolveId ` +
            `returned null for every one of them after ${this.scanCount} scans.`);
    }
    isVisibleEnough(entry) {
        var _a, _b;
        if (entry.isIntersecting === false) {
            return false;
        }
        const rootHeight = (_b = (_a = entry.rootBounds) === null || _a === void 0 ? void 0 : _a.height) !== null && _b !== void 0 ? _b : QuestionVisibilityTracker.readViewHeight(entry.target.ownerDocument);
        const fillsViewport = rootHeight > 0 && entry.intersectionRect.height / rootHeight >= QUESTION_VIEWPORT_FILL_RATIO;
        return entry.intersectionRatio >= this.threshold || fillsViewport;
    }
    evaluate(questionId) {
        const entry = this.entries.get(questionId);
        if (entry == null) {
            return;
        }
        const isEligible = this.isVisibleEnough(entry) && this.isTabFocused;
        const currentState = this.state.get(questionId);
        if (isEligible && currentState === "hidden") {
            if (this.pending.has(questionId)) {
                return;
            }
            const clientTime = new Date().toISOString();
            const timerId = setTimeout(() => {
                this.pending.delete(questionId);
                this.state.set(questionId, "shown");
                this.emitShown(questionId, clientTime);
            }, this.dwellMs);
            this.pending.set(questionId, timerId);
            return;
        }
        if (isEligible === false) {
            this.cancelPending(questionId);
            if (currentState === "shown") {
                this.state.set(questionId, "hidden");
            }
        }
    }
    emitShown(questionId, clientTime) {
        this.onShown(questionId, { clientTime });
    }
    cancelPending(questionId) {
        const timerId = this.pending.get(questionId);
        if (timerId == null) {
            return;
        }
        clearTimeout(timerId);
        this.pending.delete(questionId);
    }
    cancelAllPending() {
        this.pending.forEach((timerId) => clearTimeout(timerId));
        this.pending.clear();
    }
}
QuestionVisibilityTracker.SCANS_BEFORE_WARNING = 30;
export default QuestionVisibilityTracker;
